import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navigation from '@/components/Navigation';
import { Heart, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { copy } from '@/copy';

const AboutPage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Navigation />
      
      <main className="max-w-2xl mx-auto px-4 py-12 sm:py-16">
        {user && (
          <div className="mb-8">
            <Link to="/dashboard" data-testid="about-back-dashboard-link">
              <Button
                variant="outline"
                className="border-[#1D2D44] text-[#1D2D44] hover:bg-white rounded-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        )}
        <article className="animate-fade-in">
          {/* Page Title */}
          <header className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl text-[#1D2D44] mb-3">
              About Our Love <Heart className="inline-block w-6 h-6 sm:w-7 sm:h-7 text-[#C9A84C] mx-1" strokeWidth={1.5} /> Connection
            </h1>
          </header>

          {/* Content */}
          <div className="prose prose-lg max-w-none space-y-6 text-[#1D2D44] leading-relaxed">
            <p>
              {copy.about.content.intro}
            </p>

            <p>
              {copy.about.content.problem}
            </p>

            <p className="font-medium text-[#1D2D44]">
              {copy.about.content.different}
            </p>

            <p>
              {copy.about.content.solution}
            </p>

            <p>
              {copy.about.content.audience}
            </p>

            <p>
              {copy.about.content.values}
            </p>

            <p>
              {copy.about.content.closing}
            </p>

            <p className="font-serif text-xl text-[#1D2D44] italic pt-4">
              {copy.about.content.tagline}
            </p>
          </div>

          {/* A Note From the Founder */}
          <div className="mt-16 pt-12 border-t border-[#C9A84C]/40">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1D2D44] mb-6">
              A Note From the Founder
            </h2>
            <div className="space-y-4 text-[#1D2D44] leading-relaxed">
              <p className="italic">
                &ldquo;I built Our Love <span className="text-[#C9A84C]">&#9829;</span> Connection because I believe intentional people deserve an intentional platform. Every feature, every question, every checkpoint was designed with one goal &mdash; to help relationship-minded singles stop searching and start building something real. If you&rsquo;re here, you already know the difference. Welcome.&rdquo;
              </p>
              <p className="font-serif text-lg text-[#1D2D44]">
                &mdash; TK, Founder of Our Love <span className="text-[#C9A84C]">&#9829;</span> Connection
              </p>
            </div>
          </div>
          <div className="border-t border-[#C9A84C]/40 mt-12"></div>

          {/* How It Works Section */}
          <div className="mt-16 pt-12 border-t border-[#E5E5E5]">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1D2D44] mb-8">
              {copy.about.howItWorks.title}
            </h2>
            
            <div className="space-y-6 text-[#1D2D44] leading-relaxed">
              <p>
                Our Love <span className="text-[#C9A84C]">&#9829;</span> Connection{copy.about.howItWorks.intro.substring(copy.about.howItWorks.intro.indexOf('Connection') + 10)}
              </p>

              <p>
                {copy.about.howItWorks.realPeople}
              </p>

              <p>
                {copy.about.howItWorks.profiles}
              </p>

              <p className="font-medium text-[#1D2D44]">
                {copy.about.howItWorks.different}
              </p>

              <p>
                {copy.about.howItWorks.reflection}
              </p>

              <p>
                {copy.about.howItWorks.introduction}
              </p>

              <p>
                {copy.about.howItWorks.structure}
              </p>

              <p>
                {copy.about.howItWorks.approach}
              </p>

              <p className="text-[#424E59] italic">
                {copy.about.howItWorks.notForYou}
              </p>

              <p className="font-medium text-[#1D2D44]">
                {copy.about.howItWorks.forYou}
              </p>
            </div>
          </div>
        </article>

        <p className="mt-12 text-center text-[#424E59] text-sm">
          Questions? Reach us at <a href="mailto:hello@ourloveconnection.co" className="text-[#B89B5E] underline">hello@ourloveconnection.co</a>
        </p>
      </main>

      {/* Simple Footer */}
      <footer className="py-8 px-4 border-t border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#424E59] text-sm">
            {copy.brand.copyright(new Date().getFullYear())}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
